import {ApiSorts} from '@/api/meta/odata';
import {defineStore, StoreGeneric} from 'pinia';
import {ref, Ref, computed} from 'vue';

interface BaseStorageStore extends StoreGeneric {
  $id: string,
  count: number | undefined,
  initialized: boolean,
  loading: boolean,
  error: Error | null,
  limit: number | null,
  page: number,
  skip: number | undefined,
  pageCount: number,
  displayFilters: FilterDisplayItemExtended[],
  fetch: () => Promise<void>,
  unsetFilter: (filterToUnset: FilterDisplayItemExtended) => void,
}

interface RawStorageStore<F, S, T> {
  data: T,
  filters: F,
  sorts: S,
  defaultFilters: F,
  resetFilter: (key: keyof F | null) => void,
  filterSort: ([newFilters, newSorts]: [F, S]) => Promise<void>,
  replace: ([data, count]: [T, number | undefined]) => Promise<void>,
}

export interface StorageStore<F, S, T> extends BaseStorageStore, RawStorageStore<F, S, T> {
}

export interface SubmitRequest<T> {
  version: number;
  data: T;
  count?: number;
}

export type FilterFormatter<Filter> = {[Key in keyof Filter]?: string |
  ((value: Filter[Key]) => string | FilterDisplayItem | FilterDisplayItem[] | undefined) }

export function homogenizeFilterFormatter(val: string | FilterDisplayItem | FilterDisplayItem[] | undefined): FilterDisplayItem[] | undefined {
  if (val === undefined) return undefined;
  else if (typeof val === 'string') {
    return [{text: val}];
  } else if (Array.isArray(val)) {
    return val.length > 0 ? val : undefined;
  } else {
    return [val];
  }
};
function filterToDisplay<Filter>(
    filter: Filter,
    formatter: FilterFormatter<Filter>): FilterDisplayItemExtended[] {
  const result: FilterDisplayItemExtended[] = [];

  for (const key in filter) {
    if (!(key in formatter)) continue;
    let displays: FilterDisplayItem[] | undefined;
    if (typeof formatter[key] === 'string') {
      displays = [{
        // @ts-expect-error
        text: formatter[key],
      }];
    } else {
      // @ts-expect-error
      displays = homogenizeFilterFormatter(formatter[key](filter[key]));
    }
    if (displays === undefined) continue;
    result.push(...displays.map((val)=>{
      const keypath: string[] = val.keypath ?? [];
      keypath.unshift(key);
      return {...val, ref: filter, keypath};
    }));
  }

  return result;
}

export interface FilterDisplayItemExtended extends FilterDisplayItem {
  ref: any;
}

export interface FilterDisplayItem {
  text: string;
  color?: string;
  textClass?: string;
  icon?: string;
  keypath?: string[];
}

function combineSorts<T extends string, S extends ApiSorts<T>>(...sorts: Array<ApiSorts<T> | undefined>): S {
  const found: Partial<Record<string, boolean>> = {};
  const result: ApiSorts<T> = [];

  // eslint-disable-next-line guard-for-in
  for (const i in sorts) {
    const currentSortArray = sorts[i];
    if (currentSortArray === undefined) continue;
    // eslint-disable-next-line guard-for-in
    for (const iInner in currentSortArray) {
      const [key, order] = currentSortArray[iInner];
      const lowerKey = key.toLowerCase();
      if (found[lowerKey]) continue;
      found[lowerKey] = true;

      result.push([key, order]);
    }
  }

  return result as S;
}

function recursiveRemoveUndefined(obj: any) {
  for (const key in obj) {
    if (typeof obj[key] === 'object') {
      recursiveRemoveUndefined(obj[key]);
    }
  }
}


export function createVersionedStorage<FilterType extends Record<string, any>, SortType extends ApiSorts, T>(
    storeId: string,
    params: {
      fetchFn: (filters: FilterType, sorts: SortType, limit: number | null, skip?: number) => Promise<[T, number?]>;
      defaultData: T;
      defaultFilters: FilterType;
      defaultSorts: SortType;
      alwaysSorted?: SortType;
      defaultLimit?: number | null;
      formatter?: FilterFormatter<FilterType>;
      defaultCount?: number;
    },
) {
  const {
    fetchFn,
    defaultData,
    defaultSorts,
    alwaysSorted,
    defaultLimit = null,
    formatter,
    defaultCount,
  } = params;
  return defineStore(storeId, () => {
    const data = ref<T>(defaultData) as Ref<T>;
    const count = ref(defaultCount);
    const initialized = ref(false);
    const loading = ref(true);
    let version = 0;
    const error = ref<Error | null>(null);
    const pageInternal = ref(0);

    const limitInternal = ref(defaultLimit);
    const filtersInternal = ref<FilterType>(params.defaultFilters) as Ref<FilterType>;
    const sorts = ref<SortType>(combineSorts(defaultSorts, alwaysSorted)) as Ref<SortType>;

    const limitWrapper = computed({
      set(newLimit: number | null) {
        limitInternal.value = newLimit;
        fetch();
      },
      get() {
        return limitInternal.value;
      },
    });
    const pageWrapper = computed({
      set(newPage: number) {
        pageInternal.value = newPage;
        fetch();
      },
      get() {
        return pageInternal.value;
      },
    });

    const filters = computed({
      set(newFilters: FilterType) {
        filtersInternal.value = newFilters;
        recursiveRemoveUndefined(filtersInternal.value);
        pageWrapper.value = 0;
      },
      get() {
        return filtersInternal.value;
      },
    });
    const skip = computed(() => {
      if (!limitWrapper.value) return undefined;
      return pageWrapper.value * limitWrapper.value;
    });
    const pageCount = computed(() => {
      if (!limitWrapper.value || !count.value) return 1;

      return Math.ceil(count.value / limitWrapper.value);
    });
    const displayFilters = computed(() => {
      if (formatter) return filterToDisplay(filters.value, formatter);
      return [];
    });
    const defaultFilters = computed(() => {
      return params.defaultFilters;
    });

    function submitRequest(request: SubmitRequest<T>) {
      if (request.version !== version) {
        console.warn('Received outdated data, throwing away', request);
        return;
      }

      error.value = null;
      data.value = request.data;
      count.value = request.count;
      initialized.value = true;
      loading.value = false;
    };
    function submitError(request: SubmitRequest<Error>) {
      if (request.version !== version) {
        console.warn('Received outdated error, throwing away');
        return;
      }

      error.value = request.data;
      data.value = defaultData;
      count.value = defaultCount;
      initialized.value = true;
      loading.value = false;
    };

    function resetFilter(newFilter: (keyof FilterType)|null) {
      if (!newFilter) {
        filters.value = structuredClone(defaultFilters.value);
        return;
      }

      filters.value[newFilter] = defaultFilters.value[newFilter];
    };

    async function fetch() {
      const versionNew = ++version;
      try {
        loading.value = true;
        const [data, count] = await fetchFn(filters.value, sorts.value, limitWrapper.value, skip.value);
        submitRequest({
          data,
          count,
          version: versionNew,
        });
      } catch (err) {
        console.error('Error fetching data:', err);
        submitError({
          data: err as Error,
          version: versionNew,
        });
      }
    };

    async function filterSort([newFilters, newSorts]: [FilterType, SortType]) {
      filters.value = newFilters;
      sorts.value = combineSorts(newSorts, alwaysSorted) as SortType;
      fetch();
    };
    async function replace([data, count]: [T, number|undefined]) {
      if (!data) throw new Error('Kann Daten nicht mit nichts ersetzen');
      const versionNew = ++version;
      submitRequest({
        data,
        count,
        version: versionNew,
      });
    };

    function unsetFilter(filterToUnset: FilterDisplayItemExtended) {
      const newFilter = structuredClone(filterToUnset.ref);
      // keypath hat minimum 1 element von der filterToDisplay funktion
      if (filterToUnset.keypath && filterToUnset.keypath.length > 1) {
        let objUnderPath = newFilter[filterToUnset.keypath![0]];
        // Schleife bis zur vorletzten Key, so dass ich kann 'delete objUnderPath[lastKey]' machen
        for (let i = 1; i < filterToUnset.keypath.length - 2; i++) {
          const key = filterToUnset.keypath[i];
          const maybe = objUnderPath[key];
          if (maybe !== undefined) {
            objUnderPath = maybe;
          } else break;
        }

        const lastKey = filterToUnset.keypath[filterToUnset.keypath.length - 1];
        if (objUnderPath[lastKey] === undefined) return;
        else if (Array.isArray(objUnderPath[lastKey])) {
          objUnderPath[lastKey] = [];
        } else {
          if (Array.isArray(objUnderPath)) {
            const index = parseInt(lastKey);
            objUnderPath.splice(index, 1);
          } else {
            delete objUnderPath[lastKey];
          }
        }
      } else if (Array.isArray(newFilter[filterToUnset.keypath![0]])) {
        newFilter[filterToUnset.keypath![0]] = [];
      } else {
        delete newFilter[filterToUnset.keypath![0]];
      }
      filters.value = newFilter;
    }

    return {
      data,
      count,
      initialized,
      loading,
      error,
      filters,
      sorts,
      limit: limitWrapper,
      page: pageWrapper,
      skip,
      pageCount,
      displayFilters,
      defaultFilters,
      resetFilter,
      fetch,
      filterSort,
      replace,
      unsetFilter,
    };
  }) as unknown as () => StorageStore<FilterType, SortType, T>;
}
